import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import api from "../../services/api";
import Chart from "chart.js/auto";
import ChartDataLabels from 'chartjs-plugin-datalabels';

const SETOR_COLORS = {
    Tecelagem: "#2563eb",
    Revisão: "#8e44ad",
    Acabamento: "#e67e22",
    Tingimento: "#1abc9c",
    "Expedição de Tecido": "#e74c3c",
};
const DEFAULT_COLOR = "#64748b";
const DEFAULT_META = 1_100_000;

const fmtNum = (v) => Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(v);

const metaLinePlugin = {
    id: "metaLine",
    afterDraw(chart) {
        const metaValue = chart.options?.plugins?.metaLine?.value;
        if (!metaValue) return;

        const { ctx, chartArea, scales } = chart;
        const x = scales.x.getPixelForValue(metaValue);
        if (x < chartArea.left || x > chartArea.right) return;

        const color = chart.options.plugins.metaLine.color || "#f59e0b";

        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([8, 5]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.fillStyle = color;
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(`META: ${metaValue/1000}K m`, x + 6, chartArea.top + 16);
        ctx.restore();
    },
};

if (!Chart.registry.plugins.get("metaLine")) {
    Chart.register(metaLinePlugin);
}

function useBarChart(canvasRef, dados, meta, cor) {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !dados?.length) return;

        const labels = dados.map((d) => d.mes.replace("/de ", "/"));
        const values = dados.map((d) => d.valor);
        const bgColors = values.map((v) => meta && v >= meta ? cor : `${cor}88` );
        const maxVal = Math.max(...values, meta || 0);

        const config = {
            type: "bar",
            data: {
                labels,
                datasets: [
                    {
                        label: "",
                        data: values,
                        backgroundColor: bgColors,
                        borderRadius: 3,
                        borderSkipped: false,
                    },
                ],
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 400 },
                plugins: {
                    legend: { display: false },
                    annotation: false,
                    tooltip: {
                        callbacks: {
                            label: (ctx) => ` ${fmtNum(ctx.raw)} m`,
                        },
                    },
                    metaLine: meta ? { value: meta, color: "#f59e0b" } : { value: null },
                    datalabels: {
                        align: (ctx) => {
                            const value = ctx.dataset.data[ctx.dataIndex];
                            return value < 200000 ? 'right' : 'start';
                        },
                        anchor: (ctx) => {
                            const value = ctx.dataset.data[ctx.dataIndex];
                            return value < 200000 ? 'end' : 'end';
                        },
                        color: (ctx) => {
                            const value = ctx.dataset.data[ctx.dataIndex];
                            return value < 200000 ? '#64748b' : '#fff';
                        },
                        offset: 4,
                        formatter: (value) => `${fmtNum(value)} m`, 
                        font: {
                            size: 13,
                            weight: 'bold'
                        },
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        max: meta ? maxVal * 1.20 : maxVal * 1.15,
                        ticks: {
                            callback: (v) => `${(v / 1000).toFixed(0)}K`,
                            font: { size: 12 },
                        },
                        grid: { color: "#e2e8f0" },
                    },
                    y: {
                        ticks: { font: { size: 12 } },
                        grid: { display: false },
                    },
                },
            },
            plugins: [metaLinePlugin, ChartDataLabels]
        };

        if (chartRef.current) {
            chartRef.current.destroy();
        }
        chartRef.current = new Chart(canvasRef.current, config);

        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [canvasRef, dados, meta, cor]); 
}

const SetorChart = ({ setor, dados, cor }) => {
    const [meta, setMeta] = useState(DEFAULT_META);
    
    const canvasRef = useRef(null);
    useBarChart(canvasRef, dados, meta, cor);

    const values = dados.map((d) => d.valor);
    const media = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    const mesesAtingidos = meta ? values.filter((v) => v >= meta).length : 0;
    const pctMeta = meta ? Math.round((media / meta) * 100) : null;
    const height = Math.max(dados.length * 32, 130);

    return (
        <div
            className="setor-chart-container"
            style={{
                background: "#fff",
                borderRadius: 10,
                padding: "16px 20px 12px",
                marginBottom: 20,
                boxShadow: "0 1px 6px rgba(0,0,0,.08)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                    flexWrap: "wrap",
                    gap: 8,
                }}
            >
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
                    {setor}
                </span>
                
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                    <small style={{ color: "#64748b" }}>
                        Média: <b style={{ color: "#1e293b" }}>{fmtNum(media)} m</b>
                    </small>

                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", padding: "4px 8px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                        <small style={{ color: "#64748b", fontWeight: 600 }}>Meta:</small>
                        <InputNumber
                            value={meta}
                            onValueChange={(e) => setMeta(e.value || 0)}
                            mode="decimal"
                            minFractionDigits={0}
                            className="p-inputtext-sm"
                            inputStyle={{ padding: "0.25rem 0.5rem", width: "100px", fontSize: "12px", textAlign: "right" }}
                        />
                        <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 600 }}>m</span>
                    </div>

                    {meta > 0 && (
                        <>
                            <small style={{ color: "#64748b" }}>
                                <b style={{ color: pctMeta >= 100 ? "#16a34a" : "#dc2626" }}>
                                    {pctMeta}% da meta
                                </b>
                            </small>
                            <small style={{ color: "#64748b" }}>
                                <b style={{ color: mesesAtingidos === values.length ? "#16a34a" : "#dc2626" }}>
                                    {mesesAtingidos}/{values.length}
                                </b>{" "}
                                meses atingidos
                            </small>
                        </>
                    )}
                </div>
            </div>

            <div style={{ height, position: "relative" }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

const toApiDate = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");

const mesAbreviado = (isoDate) => {
    const d = new Date(isoDate + "T12:00:00");
    const label = d.toLocaleDateString("pt-BR", {
        month: "short",
        year: "2-digit",
    });
    return (
        label.charAt(0).toUpperCase() +
        label.slice(1).replace(".", "").replace(" ", "/")
    );
};

const MESES_IDX = {
    Jan: 0, Fev: 1, Mar: 2, Abr: 3, Mai: 4, Jun: 5,
    Jul: 6, Ago: 7, Set: 8, Out: 9, Nov: 10, Dez: 11,
};

const parseMesLabel = (s) => {
    const [m, y] = s.split("/");
    return new Date(2000 + parseInt(y), MESES_IDX[m] ?? 0, 1);
};

export const GraficoPorSetor = ({ visible, onHide }) => {
    const toast = useRef();

    const showError = (error) =>
        toast.current.show({
            summary: "Erro",
            severity: "error",
            detail: error.response ? error.response.data.message : error.message,
        });

    const today = new Date();
    const ultimoDiaDoMesAtual = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const mesAcabou = today.getDate() === ultimoDiaDoMesAtual;

    const defaultFinal = new Date(today);

    if (!mesAcabou) { defaultFinal.setMonth(defaultFinal.getMonth() - 1); }
    defaultFinal.setDate(1); 

    const defaultStart = new Date(defaultFinal);
    defaultStart.setMonth(defaultStart.getMonth() - 11);
    defaultStart.setDate(1);

    const [initialDate, setInitialDate] = useState(defaultStart);
    const [finalDate, setFinalDate] = useState(defaultFinal);

    const [setoresData, setSetoresData] = useState({});
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);

    const formatDate = (d) => new Intl.DateTimeFormat('pt-BR').format(new Date(d));

    const formatarMesAno = (date) => {
        if (!date) return "";
        const mes = date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
        const ano = date.toLocaleDateString("pt-BR", { year: "2-digit" });
        return mes.charAt(0).toUpperCase() + mes.slice(1) + "/" + ano;
    };

    const buildSetoresData = useCallback((rawDados) => {
        const map = {};
        rawDados.forEach(({ data, setor, soma }) => {
            const label = mesAbreviado(data);
            if (!map[setor]) map[setor] = {};
            map[setor][label] = (map[setor][label] || 0) + parseFloat(soma);
        });

        const result = {};
        Object.keys(map).forEach((setor) => {
            const sorted = Object.keys(map[setor]).sort( (a, b) => parseMesLabel(a) - parseMesLabel(b) );
            result[setor] = sorted.map((mes) => ({
                mes,
                valor: Math.round(map[setor][mes]),
            }));
        });

        setSetoresData(result);
    }, []);

    const handleSearch = useCallback(() => {
        setLoading(true);
        const finalBusca = new Date(finalDate.getFullYear(), finalDate.getMonth() + 1, 0);
        
        api.get("balanceamento-producao", {
            params: {
                data_inicial: toApiDate(initialDate),
                data_final: toApiDate(finalBusca),
            },
        })
            .then((r) => buildSetoresData(r.data.dados))
            .catch(showError)
            .finally(() => setLoading(false));
    }, [initialDate, finalDate, buildSetoresData]);

    const handlePrint = () => {
        setPrinting(true);
        try {
            const printElement = document.getElementById("porSetor");
            if (!printElement) return;

            const clone = printElement.cloneNode(true);
            const original = printElement.querySelectorAll("canvas");
            const clonado = clone.querySelectorAll("canvas");

            original.forEach((canvas, index) => {
                const img = document.createElement("img");
                img.src = canvas.toDataURL("image/png");
                img.style.width = "100%";
                img.style.height = "auto";
                clonado[index].parentNode.replaceChild(img, clonado[index]);
            });

            const printWindow = window.open("", "_blank");
            
            const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map(styleSheet => styleSheet.outerHTML).join("");

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                    <head>
                        <title>Impressão - Balanceamento por Setor</title>
                        ${styles}
                        <style>
                            body { 
                                background: #fff !important; 
                                padding: 20px; 
                                font-family: sans-serif; 
                            }
                            @media print {
                                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                                /* Evita que o gráfico seja cortado ao meio na mudança de página */
                                .setor-chart-container { page-break-inside: avoid; }
                            }
                        </style>
                    </head>
                    <body>
                        <h2 style="text-align: center; color: #1e293b; margin-bottom: 24px;">
                            Balanceamento de Produção por Setor — ${formatarMesAno(initialDate)} até ${formatarMesAno(finalDate)}
                        </h2>

                        <div style="margin-bottom: 15px; font-size: 12px; color: #64748b;">
                            Criado em: ${formatDate(today)}
                        </div>

                        <div style="margin-top: 16px;">
                            ${clone.outerHTML}
                        </div>
                    </body>
                </html>
            `);

            printWindow.document.close();

            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
                setPrinting(false);
            }, 500);

        } catch (error) {
            console.error("Erro na impressão:", error);
            setPrinting(false);
        }
    };

    useEffect(() => {
        if (visible) handleSearch();
    }, [visible, handleSearch]);

    const setoresList = Object.keys(setoresData);

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                header={
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <i
                            className="pi pi-chart-bar"
                            style={{ fontSize: 18, color: "#2563eb" }}
                        />
                        <span style={{ fontWeight: 700, fontSize: 17 }}>
                            Balanceamento de Produção — Por Setor
                        </span>
                    </div>
                }
                visible={visible}
                onHide={onHide}
                style={{ width: "min(95vw, 1050px)" }}
                maximizable
                modal
                draggable={false}
            >
                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                        marginBottom: 16,
                        background: "#f8fafc",
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                    }}
                >
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>
                        Período:
                    </label>
                    <Calendar
                        value={initialDate}
                        onChange={(e) => setInitialDate(e.value)}
                        dateFormat="mm/yy"
                        view="month"
                        placeholder="Mês inicial"
                        style={{ width: 80 }}
                    />
                    <span style={{ color: "#94a3b8" }}>até</span>
                    <Calendar
                        value={finalDate}
                        onChange={(e) => setFinalDate(e.value)}
                        dateFormat="mm/yy"
                        view="month"
                        placeholder="Mês final"
                        style={{ width: 80 }}
                    />
                    <Button
                        icon={"pi pi-search"}
                        onClick={handleSearch}
                        disabled={loading || printing}
                    />
                    <Button 
                        className="p-m-1"
                        icon={printing ? "pi pi-spin pi-spinner" : "pi pi-print"}
                        label={printing ? "Imprimindo" : ""}
                        onClick={handlePrint}
                        disabled={loading || printing || setoresList.length === 0}
                    />
                </div>

                <Divider style={{ margin: "8px 0 16px" }} />
                
                {loading ? (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "40px",
                            gap: "10px",
                        }}
                    >
                        <i
                            className="pi pi-spin pi-spinner"
                            style={{ fontSize: "3rem" }}
                        ></i>

                        <p style={{fontSize: "20px"}}>Carregando...</p>
                    </div>
                ) : (
                    <div id="porSetor" style={{ width: "100%", paddingBottom: "10px" }}>
                        {setoresList.length === 0 && !loading && (
                            <div
                                style={{
                                    textAlign: "center",
                                    color: "#94a3b8",
                                    padding: 40,
                                    fontSize: 14,
                                }}
                            >
                                <i className="pi pi-inbox"  style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                                Nenhum dado encontrado. Selecione o período e clique em Pesquisar.
                            </div>
                        )}

                        {setoresList.map((setor) => {
                            const cor = SETOR_COLORS[setor] || DEFAULT_COLOR;
                            return (
                                <SetorChart
                                    key={setor}
                                    setor={setor}
                                    dados={setoresData[setor]}
                                    cor={cor}
                                />
                            );
                        })}
                    </div>
                )}
            </Dialog>
        </>
    );
};